import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type {
  InvestorPortalFeature,
  InvestorPortalSettings,
  InvestorPortalSettingsResponse,
} from "../../../../api/types/investorPortalTypes";
import { allInvestorPortalFeatures } from "../../../../api/types/investorPortalTypes";
import type { PortalPageStatus } from "../pages/portalPageStatusHelper";
import PortalPageStatusSelect from "../pages/PortalPageStatusSelect";

interface Props {
  page: InvestorPortalFeature;
  pageStatus: PortalPageStatus;
  portalSettings: InvestorPortalSettings;
  onPortalSettingsUpdated: (updated: InvestorPortalSettingsResponse) => void;
}

const updateInvestorPortalSettings = withErrorHandling(adminApi.updateInvestorPortalSettings);

const PortalWidgetPageSettings = ({ page, pageStatus, portalSettings, onPortalSettingsUpdated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [editedStatus, setEditedStatus] = useState<PortalPageStatus>(pageStatus);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string>();

  const isDirty = editedStatus !== pageStatus;

  const handleStatusChange = (status: PortalPageStatus) => {
    setEditedStatus(status);
    setValidationError(undefined);
  };

  const handleReset = () => {
    setEditedStatus(pageStatus);
    setValidationError(undefined);
  };

  const handleSave = async () => {
    let liveFeatures: InvestorPortalFeature[] = portalSettings.enabledFeatures?.includes("All")
      ? [...allInvestorPortalFeatures]
      : [...(portalSettings.enabledFeatures ?? [])];
    let previewFeatures = [...(portalSettings.enabledPreviewOnlyFeatures ?? [])];

    liveFeatures = liveFeatures.filter((f) => f !== page);
    previewFeatures = previewFeatures.filter((f) => f !== page);

    if (editedStatus === "live") {
      liveFeatures.push(page);
    } else if (editedStatus === "preview") {
      previewFeatures.push(page);
    }

    if (liveFeatures.length === 0) {
      setValidationError("At least one page must be live.");
      return;
    }

    setSaving(true);
    const [updatedSettings, error] = await updateInvestorPortalSettings({
      enabledFeatures: liveFeatures,
      enabledPreviewOnlyFeatures: previewFeatures,
    });
    setSaving(false);

    if (error) {
      logError(error, "[PortalWidgetPageSettings] updateInvestorPortalSettings");
      sendNotificationError(error.message);
      return;
    }

    onPortalSettingsUpdated(updatedSettings);
    setValidationError(undefined);
    sendNotification("Page settings saved.");
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="subtitle1">Page Settings</Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2, flex: "1 1 auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography>Page status</Typography>
          <PortalPageStatusSelect value={editedStatus} onChange={handleStatusChange} />
        </Box>
        {validationError && (
          <Typography color="error" variant="caption">
            {validationError}
          </Typography>
        )}
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 3, py: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!isDirty} loading={saving}>
          Save
        </Button>
        <Button variant="text" color="secondary" onClick={handleReset} disabled={!isDirty || saving}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default PortalWidgetPageSettings;
